import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Seo from "@/components/Seo";
import FoundingOfferStep from "@/app/components/FoundingOfferStep";
import { api } from "@/lib/api";
import { Clock, CheckCircle2, XCircle, ArrowLeft, Gift } from "lucide-react";

const PLAN_LABEL = { growth: "Growth", business: "Business" };

export default function FoundingAccessPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [request, setRequest] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/founding/request/mine");
        if (alive) setRequest(data.request || null);
      } catch { /* silent */ }
      finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, []);

  if (loading) {
    return <div className="p-8 text-sm text-muted-foreground" data-testid="founding-page-loading">Loading…</div>;
  }

  if (request && request.status === "pending") {
    return (
      <div className="mx-auto max-w-2xl p-6 sm:p-8" data-testid="founding-page-pending">
        <Seo title="Launch Offer — Under review" description="Your Launch Offer application is under review." path="/app/founding" />
        <div className="rounded-3xl border border-border bg-card p-6 sm:p-8">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-orange-500/10 px-3 py-1 text-[11px] font-bold uppercase tracking-wide text-brand-orange">
            <Clock className="h-3 w-3" /> Under review
          </span>
          <h1 className="mt-4 font-display text-2xl font-semibold tracking-tight">Launch Offer request — under review</h1>
          <p className="mt-2 text-sm text-muted-foreground">Thanks for applying. Requested plan: <strong>{PLAN_LABEL[request.requested_plan] || request.requested_plan}</strong>. We'll notify you once it's reviewed. You're on the Free plan meanwhile.</p>
          <button onClick={() => navigate("/app")} className="mt-5 inline-flex items-center gap-1.5 rounded-full border border-border bg-background px-4 py-2 text-sm font-semibold hover:bg-muted" data-testid="founding-back-workspace"><ArrowLeft className="h-3.5 w-3.5" /> Back to Workspace</button>
        </div>
      </div>
    );
  }

  if (request && request.status === "approved") {
    return (
      <div className="mx-auto max-w-2xl p-6 sm:p-8" data-testid="founding-page-approved">
        <Seo title="Launch Offer active" description="Your Launch Offer is active." path="/app/founding" />
        <div className="rounded-3xl border border-brand-teal/40 bg-brand-teal/5 p-6 sm:p-8">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-brand-teal/20 px-3 py-1 text-[11px] font-bold uppercase tracking-wide text-brand-teal">
            <CheckCircle2 className="h-3 w-3" /> Launch Offer active
          </span>
          <h1 className="mt-4 font-display text-2xl font-semibold tracking-tight">Your Launch Offer is active 🎉</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Complimentary <strong>{PLAN_LABEL[request.approved_plan] || request.approved_plan}</strong> plan active until <strong>{(request.approved_expires_at || "").slice(0, 10)}</strong>.
          </p>
          <div className="mt-5 flex flex-wrap gap-2">
            <button onClick={() => navigate("/app/settings/billing")} className="inline-flex items-center gap-1.5 rounded-full brand-gradient-bg px-4 py-2 text-sm font-semibold text-white" data-testid="founding-open-billing">Explore your plan</button>
            <button onClick={() => navigate("/app")} className="inline-flex items-center gap-1.5 rounded-full border border-border bg-background px-4 py-2 text-sm font-semibold hover:bg-muted"><ArrowLeft className="h-3.5 w-3.5" /> Back to Workspace</button>
          </div>
        </div>
      </div>
    );
  }

  if (request && request.status === "declined") {
    return (
      <div className="mx-auto max-w-2xl p-6 sm:p-8" data-testid="founding-page-declined">
        <div className="rounded-3xl border border-border bg-card p-6 sm:p-8">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-muted px-3 py-1 text-[11px] font-bold uppercase tracking-wide text-muted-foreground">
            <XCircle className="h-3 w-3" /> Not approved
          </span>
          <h1 className="mt-4 font-display text-2xl font-semibold tracking-tight">Your previous application wasn't approved this round</h1>
          <p className="mt-2 text-sm text-muted-foreground">You can continue on the Free plan or explore paid plans anytime.</p>
          <button onClick={() => navigate("/app")} className="mt-5 inline-flex items-center gap-1.5 rounded-full border border-border bg-background px-4 py-2 text-sm font-semibold hover:bg-muted"><ArrowLeft className="h-3.5 w-3.5" /> Back to Workspace</button>
        </div>
      </div>
    );
  }

  // No request yet — the acquisition campaign is retired.
  return (
    <div className="mx-auto max-w-2xl p-6 sm:p-8" data-testid="founding-page-closed">
      <Seo title="Launch Offer" description="The Workspace Launch Offer is closed." path="/app/founding" />
      <div className="rounded-3xl border border-border bg-card p-6 sm:p-8">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-muted px-3 py-1 text-[11px] font-bold uppercase tracking-wide text-muted-foreground">
          <Gift className="h-3 w-3" /> Launch Offer · closed
        </span>
        <h1 className="mt-4 font-display text-2xl font-semibold tracking-tight">The Launch Offer has ended</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Thanks for your interest. New applications are no longer accepted, but you can start on the free Starter plan or subscribe to Growth or Business anytime.
        </p>
        <div className="mt-5 flex flex-wrap gap-2">
          <button
            onClick={() => navigate("/app/settings/billing")}
            data-testid="founding-open-billing"
            className="inline-flex items-center gap-1.5 rounded-full brand-gradient-bg px-4 py-2 text-sm font-semibold text-white"
          >
            View plans
          </button>
          <button
            onClick={() => navigate("/app")}
            className="inline-flex items-center gap-1.5 rounded-full border border-border px-4 py-2 text-sm font-semibold hover:bg-muted"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> Back to Workspace
          </button>
        </div>
      </div>
    </div>
  );
}
